/**
 * 服务端错误码 → 学员可直接读懂的提示
 * -----------------------------------------------------------------------------
 * 口径：提示必须「说明原因 + 怎么做」（设计令牌 §6）。
 * 核验类错误**不得**被表述成「等待审核」——核验是提交前的门槛，
 * 未达标就要当场引导回去重拍 / 重填。
 */
import { type AnswerError, ERROR_MESSAGE_SUGGESTION, ErrorCode, KycStep } from '@enroll/shared';
import { ApiError } from './client';

/** 核验步骤中文名（与 `KycStep` 一一对应，顺序即链路顺序） */
export const KYC_STEP_LABEL: Record<KycStep, string> = {
  [KycStep.OCR]: '身份证识别',
  [KycStep.ID_LOCAL_CHECK]: '证件号校验',
  [KycStep.FACE_MATCH]: '头像比对',
  [KycStep.PHOTO_GENERATE]: '证件照生成',
  [KycStep.ADMISSION_CHECK]: '准入判定',
};

/** 某步骤失败后应引导回去做的事 */
export const KYC_STEP_ACTION: Record<KycStep, string> = {
  [KycStep.OCR]: '请重新拍摄身份证人像面',
  [KycStep.ID_LOCAL_CHECK]: '请核对并重新填写证件号',
  // 比对不通过要改的是**证件照**：身份证侧已经过质量与风险检测，重拍它没有意义
  [KycStep.FACE_MATCH]: '请按取景框重新拍摄证件照，系统会重新比对',
  [KycStep.PHOTO_GENERATE]: '请重新拍摄证件照',
  [KycStep.ADMISSION_CHECK]: '请按提示重新拍摄身份证 / 证件照后再次提交',
};

/** 核验类错误码 → 应跳回重做的步骤 */
const KYC_FAIL_STEP: Record<string, KycStep> = {
  'KYC-001': KycStep.OCR,
  'KYC-002': KycStep.OCR,
  'KYC-003': KycStep.OCR,
  'KYC-004': KycStep.OCR,
  'KYC-005': KycStep.OCR,
  'KYC-101': KycStep.ID_LOCAL_CHECK,
  'KYC-102': KycStep.ID_LOCAL_CHECK,
  'KYC-103': KycStep.ID_LOCAL_CHECK,
  'KYC-104': KycStep.ID_LOCAL_CHECK,
  'KYC-105': KycStep.ID_LOCAL_CHECK,
  'KYC-106': KycStep.ID_LOCAL_CHECK,
  'KYC-201': KycStep.FACE_MATCH,
  'KYC-202': KycStep.FACE_MATCH,
  'KYC-203': KycStep.FACE_MATCH,
  'KYC-301': KycStep.PHOTO_GENERATE,
  'KYC-302': KycStep.PHOTO_GENERATE,
  'KYC-303': KycStep.PHOTO_GENERATE,
};

/**
 * 准入未达标（`KYC-501`）时应重做的步骤 —— 由 `details.failCode` 反查。
 *
 * `KYC-501` 本身只说明「准入判定没过」，真正要改的是**被扣分的那一步**：
 *   - `failCode = KYC-003`（OCR 置信度过低）→ 重拍身份证人像面
 *   - `failCode = KYC-203`（头像相似度不足）→ 重拍证件照（比对会自动重跑）
 * 定位不到（缺 `failCode` / 未知取值）时返回 `null`：**不跳步**，
 * 只提示「按提示重新拍摄后再次提交」，避免把学员丢到一个改不了任何东西的页面上。
 *
 * 口径依据：核验是提交前门槛，未达标一律**当场提醒学员修改**，不进任何人工队列。
 */
export function kycRedoStepFor(failCode: string | null | undefined): KycStep | null {
  if (typeof failCode !== 'string') return null;
  return KYC_FAIL_STEP[failCode] ?? null;
}

/** 错误码是否属于核验类（`KYC-*`） */
export function isKycErrorCode(code: string): boolean {
  return code.startsWith('KYC-');
}

/**
 * 核验失败时应跳回的步骤；无法判定时返回 null（由调用方按当前步骤处理）。
 *
 * `KYC-501` 特判：按 `details.failCode` 定位到真正要重做的那一步，
 * 而不是「准入确认」这一步本身（那一步没有可修改的内容）。
 */
export function kycStepOf(error: ApiError): KycStep | null {
  if (error.code === 'KYC-501') {
    return kycRedoStepFor(error.details?.failCode);
  }
  const direct = KYC_FAIL_STEP[error.code];
  if (direct) return direct;
  const step = error.details?.step;
  return typeof step === 'string' && step in KYC_STEP_LABEL ? (step as KycStep) : null;
}

function missingStepsText(error: ApiError): string | null {
  const missing = error.details?.missingSteps;
  if (!Array.isArray(missing) || missing.length === 0) return null;
  const labels = missing
    .filter((step): step is KycStep => typeof step === 'string' && step in KYC_STEP_LABEL)
    .map((step) => KYC_STEP_LABEL[step]);
  if (labels.length === 0) return null;
  return `还需完成：${labels.join('、')}`;
}

/**
 * 把错误转成一句可直接展示的提示。
 *
 * 优先级：**具体错误码的分支文案** > 服务端 message > 错误码建议文案 > 泛化兜底。
 * 服务端 message 排在分支文案之后，是因为核验 / 表单这类错误要带上前端才有的
 * 上下文（如「点哪个按钮重拍」）。
 */
export function describeApiError(error: unknown): string {
  if (!(error instanceof ApiError)) return '操作失败，请稍后重试';

  const step = kycStepOf(error);

  switch (error.code) {
    case ErrorCode.KYC_ADMISSION_FAILED: {
      // 准入未达标 = 「哪一步被扣了分」→ 直接把那一步的改法说清楚，绝不写成「等待审核」
      const failCode = error.details?.failCode;
      const redoStep = kycRedoStepFor(failCode);
      if (redoStep) {
        const action = KYC_STEP_ACTION[redoStep];
        const suggestion =
          typeof failCode === 'string' ? ERROR_MESSAGE_SUGGESTION[failCode] : undefined;
        return `${suggestion ?? action}，修改后重新提交`;
      }
      const reason = error.details?.failReason;
      const base = typeof reason === 'string' && reason ? reason : '核验未达标';
      return `${base}，请按提示重新拍摄身份证 / 人脸后再次提交`;
    }
    case ErrorCode.KYC_STEP_PREREQUISITE: {
      const missing = missingStepsText(error);
      if (missing) return `${missing}，请按步骤完成核验后再提交`;
      return step ? KYC_STEP_ACTION[step] : '核验步骤尚未完成，请按顺序完成后再提交';
    }
    case ErrorCode.ENROLL_FORM_INVALID: {
      const fields = error.details?.fields;
      if (Array.isArray(fields) && fields.length > 0) {
        return '表单填写有误，请按字段提示修改后重新提交';
      }
      return '表单填写有误，请检查后重新提交';
    }
    case ErrorCode.ENROLL_FIELD_LOCKED: {
      const field = error.details?.field;
      return typeof field === 'string'
        ? `字段「${field}」已锁定，不可修改`
        : '该字段已锁定，不可修改';
    }
    case ErrorCode.ENROLL_FIELD_NEEDS_REVIEW:
      return '该字段修改后需人工复审，请联系客服';
    case ErrorCode.ENROLL_DUPLICATE:
      return '检测到重复报名，已为你打开已存在的报名单';
    case ErrorCode.AUTH_FORWARDED_LINK:
      return '请从公众号菜单进入，转发链接无法继续';
    default:
      break;
  }

  if (isKycErrorCode(error.code) && step) {
    return step ? KYC_STEP_ACTION[step] : '核验未通过，请重新拍摄';
  }

  if (error.message) return error.message;
  return ERROR_MESSAGE_SUGGESTION[error.code] ?? '操作失败，请稍后重试';
}

/** 表单字段级错误（`ENROLL-004` 的 `details.fields`）；没有则返回空数组 */
export function fieldErrorsOf(error: unknown): AnswerError[] {
  if (!(error instanceof ApiError)) return [];
  const fields = error.details?.fields;
  if (!Array.isArray(fields)) return [];
  return fields.filter(
    (item): item is AnswerError =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as AnswerError).key === 'string' &&
      typeof (item as AnswerError).message === 'string',
  );
}
